import axios from 'axios'
import type { operations } from '../../types/api.generated'
import { queryClient } from '../queryClient'

export type JsonRequest<Operation extends keyof operations> =
  operations[Operation] extends { requestBody: { content: { 'application/json': infer Body } } }
    ? Body
    : never

function redirectToLogin(): void {
  if (typeof window === 'undefined' || window.location?.pathname === '/login') {
    return
  }

  if (window.navigator.userAgent.includes('jsdom')) {
    window.history.replaceState({}, '', '/login')
    return
  }

  window.location.href = '/login'
}

/**
 * Axios instance configured for AGMS CRM API.
 *
 * Authentication: Sanctum stateful SPA cookie auth.
 * - Login establishes a Laravel session cookie that is httpOnly.
 * - Axios sends cookies on API requests and mirrors XSRF-TOKEN into
 *   X-XSRF-TOKEN for mutating requests.
 * - On 401 the cached user is cleared and the user is redirected to /login.
 */
const api = axios.create({
  baseURL: '/api',
  // Production latency (ALB + Fargate cold starts + RDS) can push a
  // simple POST past the default 0 (no timeout) browsers enforce as
  // ~10-30s via their own fetch internals. Set an explicit 30s so we
  // don't give up on a request the server is still processing.
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  withXSRFToken: true,
})

// Response interceptor for error handling.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session is missing/expired/revoked — clear cached auth state and bounce to login.
      // Skip the redirect in test environments and on the login page
      // itself to avoid a redirect loop on bad credentials.
      queryClient.setQueryData(['user'], null)
      redirectToLogin()
    }

    if (error.response?.status === 422) {
      // Validation errors are in error.response.data.errors
      return Promise.reject(error)
    }

    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

export default api
